import * as React from 'react'

import { marketApi, Site } from '@envato/foundation-types'
import { config } from '../../../../common/config'
import { IItem } from '../../../../typings/searchResults'
import { reportBlockClick } from '../../../../common/analytics/homePageAnalytics'
import { IHomePageBlockComponent, ICta } from '../../../../typings/pageBlocks'

import ItemCard from '../../itemCard'
import ItemCardsLayout from '../../itemCardsLayout'
import Card from '../../categoryPage/elementsPromoItemsBlock/card/card'
import Button from '../../button'

import * as styles from './featuredAuthorBlock.module.css'

type ReportItemClickFunction = (
  index: number,
  item: IItem,
  listName: string
) => null

interface IProps extends IHomePageBlockComponent {
  data: IItem[]
  author: marketApi.IFeaturedAuthor
  title: string
  subtitle?: string
  cta: ICta
  currentSite: Site
}

const renderSimplifiedItemCard = (reportItemClick: ReportItemClickFunction) => (
  item: IItem,
  index: number
) => (
  <div className={styles.cardWrapper} key={index}>
    <Card
      {...item}
      imageRatios={{ previewRatio: 300 / 590 }}
      title={item.name}
      author={item.author_username}
      width={33}
      itemURL={item.url}
      showCTA={false}
      onClick={() =>
        reportItemClick(
          index,
          item,
          'Block interaction;Homepage: Featured Author'
        )
      }
    />
  </div>
)

const renderStandardItemCard = () => (item: IItem, index: number) => (
  <ItemCardsLayout.CardArea key={item.id}>
    <ItemCard
      listName="Homepage: Featured Author"
      item={item}
      key={item.id}
      index={index}
      viewType="grid"
    />
  </ItemCardsLayout.CardArea>
)

const FeaturedAuthorBlock = ({
  data,
  title,
  subtitle,
  cta,
  author,
  reportItemClick,
  currentSite
}: IProps) => {
  const defaultAvatarImgSrc = `${config.assets.PUBLIC_PATH}/images/default-user.jpg`
  const avatarSrc = (author && author.profile_image_url) || defaultAvatarImgSrc
  const showSimplifiedItemCard = currentSite === Site.photodune

  return (
    <section className={styles.root} data-test-selector="featuredAuthorBlock">
      <div className={styles.wrapper}>
        <ItemCardsLayout viewType="grid" className={styles.grid}>
          <ItemCardsLayout.CardArea className={styles.textArea}>
            <header>
              <h2 className={styles.title}>{title}</h2>

              <div className={styles.textAreaInner}>
                <div className={styles.author}>
                  <img
                    className={styles.authorImage}
                    src={avatarSrc}
                    alt={author.username}
                  />

                  <div className={styles.authorDetail}>
                    <h3 className={styles.authorTitle}>{author.username}</h3>
                    <p>
                      {author.country && `${author.country}, `}Member since{' '}
                      {author.member_since}
                    </p>
                    {author.portfolio_path && (
                      <Button
                        className={styles.leftButton}
                        type={Button.Type.Primary}
                        href={cta.url}
                        onClick={() =>
                          reportBlockClick('Featured Author', {
                            eventLabel: cta.title
                          })
                        }
                      >
                        {cta.title}
                      </Button>
                    )}
                  </div>
                </div>

                {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
              </div>
            </header>
          </ItemCardsLayout.CardArea>
          {showSimplifiedItemCard
            ? data.map(renderSimplifiedItemCard(reportItemClick))
            : data.map(renderStandardItemCard())}
        </ItemCardsLayout>

        <Button
          type={Button.Type.Primary}
          className={styles.lowerButton}
          href={cta.url}
          onClick={() =>
            reportBlockClick('Featured Author', {
              eventLabel: cta.title
            })
          }
        >
          {cta.title}
        </Button>
      </div>
    </section>
  )
}

export default FeaturedAuthorBlock
